import z from 'zod';
import { passwordValidationSchema } from '@lili/lili-frontend-common';
import { useFormHook, useSetError } from '@Hooks/index';
import { OnboardingFormFields } from '@Types/index';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { DocVStatus } from '@Store/slices/ubo/createUboSlice';
import { UboOnboardingAnalyticsService } from '../UboAnalytcs.service';
import { useCompleteUbo } from './useCompleteUbo';

export interface SetPasswordFormType {
  [OnboardingFormFields.PASSWORD]: string;
}

const setPasswordFormScheme = z.object({
  [OnboardingFormFields.PASSWORD]: passwordValidationSchema,
});

export const useCreatePasswordLogic = () => {
  const defaultValues: SetPasswordFormType = {
    [OnboardingFormFields.PASSWORD]: '',
  };

  const formMethods = useFormHook({
    formSchema: setPasswordFormScheme,
    formDefaultValues: defaultValues,
    schemaTitle: 'set-password-form',
  });
  const { navigateToSpecificPage } = useNavigationManager();
  const token = useBoundStore((state) => state.uboToken ?? '');
  const setDocVData = useBoundStore((state) => state.setDocVData);
  const customerId = useBoundStore((state) => state.secondaryCustomerId!);

  const { mutate: completeUbo, isPending: isPendingCompleteUbo } = useCompleteUbo({
    customerId,
    onSuccess: (response) => {
      if (response.docVStatus === DocVStatus.APPROVED) {
        UboOnboardingAnalyticsService.completeUbo();
        navigateToSpecificPage(RoutesMap.INVITE_TEAM_MEMBER_FINISHED);
      } else if (response.docVStatus === DocVStatus.REJECTED) {
        navigateToSpecificPage(RoutesMap.VERIFICATION_FAILED);
      } else {
        setDocVData({
          url: response.url,
          qrCode: response.qrCode,
          status: response.docVStatus,
        });
        navigateToSpecificPage(RoutesMap.UBO_ONBOARDING_DOCV);
      }
    },
  });

  const handleSubmit = (values: SetPasswordFormType) => {
    UboOnboardingAnalyticsService.setPassword();

    completeUbo({
      setPasswordData: { password: values[OnboardingFormFields.PASSWORD] },
      token,
    });
  };

  useSetError({ formMethods });

  return {
    formMethods,
    handleSubmit,
    isLoading: isPendingCompleteUbo,
  };
};
